import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { FadeIn, Overline } from "../components/Motion";
import { Monogram } from "../components/Monogram";
import { OPERATORS } from "../data/operators";

const OFFICE = "https://static.prod-images.emergentagent.com/jobs/2f486ce3-6005-42d6-adf3-873498688899/images/11335a67289c830fabf5234ef64c67f31ded506428609d406b792892e13a76e4.png";

const PRINCIPLES = [
    { title: "Quiet operator model", body: "No noise. No brand theater. We work behind the scenes and execute." },
    { title: "Zero SG&A burden", body: "No salaries, no benefits, no long-term commitments. Senior execution, not headcount." },
    { title: "Senior-level execution", body: "Not junior BD reps or agencies. Real operators who have built and scaled businesses." },
    { title: "Revenue-first mindset", body: "Everything ties back to closed deals and monetization. Pipeline isn't strategy — it's outcome." },
];

const About = () => {
    return (
        <main data-testid="page-about" className="text-white">
            {/* Header band */}
            <section className="relative pt-40 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
                <div className="absolute inset-0 starfield opacity-25 fade-edges pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                    <FadeIn>
                        <Overline className="mb-8">About Orion Road</Overline>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.98] tracking-tight max-w-5xl">
                            We don't write decks. <span className="italic text-accent">We close business.</span>
                        </h1>
                    </FadeIn>
                </div>
            </section>

            {/* Philosophy + Office image */}
            <section className="relative pb-32 lg:pb-44">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="md:col-span-6 md:sticky md:top-32">
                        <FadeIn>
                            <div className="relative aspect-[4/5] w-full overflow-hidden border border-white/[0.06]">
                                <img
                                    src={OFFICE}
                                    alt="Quiet operator workspace"
                                    className="absolute inset-0 w-full h-full object-cover opacity-90"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,7,10,0.1) 0%, rgba(5,7,10,0.7) 100%)" }} />
                            </div>
                        </FadeIn>
                    </div>

                    <div className="md:col-span-6 md:pt-8">
                        <FadeIn>
                            <Overline className="mb-6">Philosophy</Overline>
                            <p className="font-display text-3xl lg:text-[2.5rem] leading-[1.2] text-white/95">
                                Orion Road is built for founders who want results, not theater.
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.1}>
                            <p className="mt-10 text-[15px] lg:text-lg leading-relaxed text-white/65 max-w-xl">
                                Founders often face the same structural gap: they need senior operators to open enterprise doors,
                                structure partnerships and close revenue — but they can't justify the cost of a full-time
                                Head of BD or CRO. The result is wasted runway, missed quarters, and a fundraising story
                                that lacks traction.
                            </p>
                        </FadeIn>
                        <FadeIn delay={0.18}>
                            <p className="mt-6 text-[15px] lg:text-lg leading-relaxed text-white/65 max-w-xl">
                                We close that gap. Senior, execution-focused business development — on demand.
                                We plug into your team, leverage existing networks and infrastructure, and convert usage
                                into actual dollars.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.26}>
                            <div className="mt-14">
                                <Link
                                    to="/contact"
                                    data-testid="about-cta-contact"
                                    className="inline-flex items-center gap-3 border border-white/25 text-white px-7 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                                >
                                    Talk to an operator
                                </Link>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* Operators */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
                        <div className="md:col-span-5">
                            <FadeIn>
                                <Overline className="mb-6">Our team</Overline>
                                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                                    The people <span className="italic text-accent">behind the work.</span>
                                </h2>
                            </FadeIn>
                        </div>
                        <div className="md:col-span-7 md:pt-6">
                            <FadeIn delay={0.1}>
                                <p className="text-[15px] lg:text-lg leading-relaxed text-white/65 max-w-xl">
                                    Orion Road is a small, senior team. We don't grow headcount, we grow outcomes.
                                    These are the operators who plug in, open doors and close business on your behalf.
                                </p>
                            </FadeIn>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06]">
                        {OPERATORS.map((op, i) => (
                            <FadeIn key={op.name} delay={i * 0.07} className="bg-[#05070A]">
                                <Link
                                    to={`/team#${op.slug}`}
                                    data-testid={`operator-${op.slug}`}
                                    className="group h-full flex flex-col p-8 lg:p-10 min-h-[340px] transition-colors hover:bg-[#0A0E15]"
                                >
                                    <span className="font-display text-2xl text-white/25">0{i + 1}</span>
                                    <h3 className="mt-10 font-display text-4xl lg:text-5xl text-white leading-[1.05]">{op.name}</h3>
                                    <p className="mt-3 text-overline">{op.role}</p>
                                    <p className="mt-6 text-[14px] leading-relaxed text-white/65">{op.bio}</p>
                                </Link>
                            </FadeIn>
                        ))}
                    </div>

                    <FadeIn delay={0.2}>
                        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                            <Link
                                to="/team"
                                data-testid="team-profiles-cta"
                                className="group inline-flex items-center gap-3 border border-white/25 text-white px-7 py-4 text-[12px] tracking-[0.26em] uppercase hover:bg-white hover:text-[#05070A] transition-colors duration-300"
                            >
                                Team profiles
                                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                            <p className="text-[13px] tracking-[0.2em] uppercase text-white/40">
                                Senior operators · Behind the scenes · Aligned with outcomes
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* Principles */}
            <section className="relative py-32 lg:py-40 border-t border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <FadeIn>
                        <Overline className="mb-4">Principles</Overline>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
                            Four principles. <span className="italic text-white/75">They shape every engagement.</span>
                        </h2>
                    </FadeIn>

                    <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06]">
                        {PRINCIPLES.map((p, i) => (
                            <FadeIn key={p.title} delay={i * 0.06} className="bg-[#05070A]">
                                <div
                                    data-testid={`principle-${i + 1}`}
                                    className="p-10 lg:p-14 h-full"
                                >
                                    <span className="font-display text-2xl text-white/30">0{i + 1}</span>
                                    <h3 className="mt-8 font-display text-3xl lg:text-4xl text-white leading-tight">{p.title}</h3>
                                    <p className="mt-5 text-[15px] leading-relaxed text-white/60 max-w-md">{p.body}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>
        </main>
    );
};

export default About;
